import LocalStorage from '@/store/LocalStorage';
import { setLocale } from '@/utils/dateHelper';
import {
  useCallback,
  useEffect,
  useMemo,
} from '@preact-signals/safe-react/react';
import { ConfigProvider } from 'antd';

import { hasHtmlTags } from '@/utils/helpers';
import 'dayjs/locale/en';
import 'dayjs/locale/it';
import { get } from 'lodash-es';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultLocale, Locale } from './config';
import { TranslationKeys } from './i18n';

interface LanguageContextProps {
  t: (i18nKey: TranslationKeys, options?: any) => React.ReactNode;
  i18n: any;
  onClickLanguageChange: (language: string) => void;
  languages: Locale[];
  language: Locale;
}
//@ts-ignore
export const LanguageContext = createContext<LanguageContextProps>(undefined);

export const LanguageContextProvider = ({ children }: { children: any }) => {
  const languages: Locale[] = ['en', 'it'];
  const { t: _t, i18n } = useTranslation();

  const language = useMemo<Locale>(
    () => i18n?.language as Locale,
    [i18n.language],
  );
  const locale = useMemo(() => {
    if (language === 'it') {
      return require('antd/es/locale/it_IT').default;
    }
    return require('antd/es/locale/en_US').default;
  }, [language]);

  const onClickLanguageChange = (language: string) => {
    //console.log('🚀 ~ onClickLanguageChange ~ language:', language);

    i18n.changeLanguage(language); //change the language
    setLocale(language);
    LocalStorage.setItem('i18nextLng', language);
  };

  const t = useCallback(
    (i18nKey: TranslationKeys, options: any = {}) => {
      const transalted = _t(i18nKey, options);
      if (hasHtmlTags(`${transalted}`)) {
        return <div dangerouslySetInnerHTML={{ __html: `${transalted}` }} />;
      } else {
        return transalted as React.ReactNode;
      }
    },
    [_t],
  );

  useEffect(() => {
    const language = LocalStorage.getItem('i18nextLng');
    //console.log('🚀 ~ useEffect ~ language:', language);
    setLocale(language);
    i18n.changeLanguage(language || defaultLocale);
  }, [i18n]);

  return (
    <LanguageContext.Provider
      value={{ t, i18n, onClickLanguageChange, languages, language }}
    >
      <ConfigProvider locale={locale}>{children}</ConfigProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = (): LanguageContextProps =>
  useContext(LanguageContext);

export const translate = (i18nKey: TranslationKeys) => {
  const language = LocalStorage.getItem('i18nextLng');
  console.log('🚀 ~ translate ~ language:', language);
  const key = `i18next_res_${language}-translation`;
  console.log('🚀 ~ translate ~ key:', key);
  try {
    const translations = LocalStorage.getItem(key, true);
    console.log('🚀 ~ translate ~ translations:', translations);
    return get(translations, i18nKey, i18nKey);
  } catch (e) {
    console.error('Error in translation', e);
    return i18nKey;
  }
};
